import { useState } from "react";
import { Actionable, Box, Button, Card, Divider, Icon, Stack, Text, Title, type IconSVG } from "@bookingcom/bui-react";
import {
  AccountUsersIcon,
  ArrowNavDownIcon,
  ArrowNavUpIcon,
  TargetCenterIcon,
  TransportAirplaneDepartIcon,
} from "@bookingcom/bui-assets-react/streamline";
import { t, useI18n, type I18nChildContext } from "@bookingcom/lingojs-react";
import styles from "./CampaignImpactMetricsExplainer.module.css";

type MetricExplanation = {
  id: string;
  icon: IconSVG;
  header: string;
  body: string;
  numerator: string;
  denominator: string;
};

const getMetricExplanationConfig = (i18n: I18nChildContext): MetricExplanation[] => [
  {
    id: "uplift-in-passengers",
    icon: AccountUsersIcon,
    header: i18n.trans(t("flights_airline_portal_metric_education_tray_uplift_passengers_header")),
    body: i18n.trans(t("flights_airline_portal_metric_education_tray_uplift_passengers_body")),
    numerator: i18n.trans(t("flights_airline_portal_metric_education_tray_uplift_passengers_calculation_part_one")),
    denominator: i18n.trans(t("flights_airline_portal_metric_education_tray_uplift_passengers_calculation_part_two")),
  },
  {
    id: "uplift-in-bookings",
    icon: TransportAirplaneDepartIcon,
    header: i18n.trans(t("flights_airline_portal_metric_education_tray_uplift_bookings_header")),
    body: i18n.trans(t("flights_airline_portal_metric_education_tray_uplift_bookings_body")),
    numerator: i18n.trans(t("flights_airline_portal_metric_education_tray_uplift_bookings_calculation_part_one")),
    denominator: i18n.trans(t("flights_airline_portal_metric_education_tray_uplift_bookings_calculation_part_two")),
  },
  {
    id: "campaign-effectiveness",
    icon: TargetCenterIcon,
    header: i18n.trans(t("flights_airline_portal_metric_education_tray_campaign_effectiveness_header")),
    body: i18n.trans(t("flights_airline_portal_metric_education_tray_campaign_effectiveness_body")),
    numerator: i18n.trans(
      t("flights_airline_portal_metric_education_tray_campaign_effectiveness_calculation_part_one")
    ),
    denominator: i18n.trans(
      t("flights_airline_portal_metric_education_tray_campaign_effectiveness_calculation_part_two")
    ),
  },
];

type CalculationFormulaProps = {
  numerator: string;
  denominator: string;
};

const CalculationFormula = ({ numerator, denominator }: CalculationFormulaProps) => (
  <Box backgroundColor="neutral_alt" borderRadius={200} mixin={{ padding: 4 }}>
    <Stack direction="row" gap={4} alignItems="center">
      <Stack.Item grow>
        <Stack direction="column" gap={1}>
          <Text variant="body_2" align="center">
            {numerator}
          </Text>
          <Divider className={styles.divider} />
          <Text variant="body_2" align="center">
            {denominator}
          </Text>
        </Stack>
      </Stack.Item>
      <Text variant="body_2">× 100%</Text>
    </Stack>
  </Box>
);

type MetricExplanationPanelProps = {
  metric: MetricExplanation;
  calculationHeader: string;
};

const MetricExplanationPanel = ({ metric, calculationHeader }: MetricExplanationPanelProps) => (
  <Stack direction="column" gap={4}>
    <Stack direction="row" gap={2} alignItems="center">
      <Text variant="strong_1">{metric.header}</Text>
      <Icon svg={metric.icon} size="medium" color="action" />
    </Stack>

    <Text variant="body_2">{metric.body}</Text>

    <Stack direction="column" gap={2}>
      <Text variant="strong_2">{calculationHeader}</Text>
      <CalculationFormula numerator={metric.numerator} denominator={metric.denominator} />
    </Stack>
  </Stack>
);

export const CampaignImpactMetricsExplainer = () => {
  const i18n = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const metrics = getMetricExplanationConfig(i18n);
  const [activeId, setActiveId] = useState(metrics[0].id);
  const activeMetric = metrics.find((metric) => metric.id === activeId) ?? metrics[0];
  const calculationHeader = i18n.trans(t("flights_airline_portal_metric_education_tray_calculation_section_header"));

  return (
    <Stack direction="column" gap={4}>
      <Button.Aligner alignment="start">
        <Button
          variant="tertiary"
          text={i18n.trans(t("flights_airline_portal_metric_education_tray_entry_point"))}
          icon={isOpen ? ArrowNavUpIcon : ArrowNavDownIcon}
          iconPosition="end"
          onClick={() => setIsOpen((open) => !open)}
          attributes={{ "aria-expanded": isOpen }}
        />
      </Button.Aligner>

      {isOpen && (
        <Stack direction="column" gap={4}>
          <Divider />

          <Stack direction="column" gap={2}>
            <Title variant="headline_3" title={i18n.trans(t("flights_airline_portal_metric_education_tray_title"))} />
            <Text variant="body_2">{i18n.trans(t("flights_airline_portal_metric_education_tray_subtitle"))}</Text>
          </Stack>

          <Card mixin={{ padding: 0 }}>
            <Stack direction="row" gap={0} alignItems="stretch" divided>
              <Stack.Item>
                <Stack direction="column">
                  {metrics.map((metric) => {
                    const isActive = metric.id === activeMetric.id;

                    return (
                      <Actionable
                        key={metric.id}
                        wide
                        onClick={() => setActiveId(metric.id)}
                        attributes={{ "aria-pressed": isActive }}
                      >
                        <Box backgroundColor={isActive ? "action_alt" : undefined} mixin={{ width: "100%" }}>
                          <Stack direction="row" gap={2} alignItems="center">
                            <Icon svg={metric.icon} size="medium" color={isActive ? "action" : "neutral"} />
                            <Text variant="emphasized_2" color={isActive ? "action" : "neutral"} wrap="nowrap">
                              {metric.header}
                            </Text>
                          </Stack>
                        </Box>
                      </Actionable>
                    );
                  })}
                </Stack>
              </Stack.Item>

              <Stack.Item
                mixin={{ paddingBlockStart: 4, paddingBlockEnd: 4, paddingInlineStart: 6, paddingInlineEnd: 6 }}
                grow
              >
                <MetricExplanationPanel metric={activeMetric} calculationHeader={calculationHeader} />
              </Stack.Item>
            </Stack>
          </Card>
        </Stack>
      )}
    </Stack>
  );
};
